import React, { createContext, useContext, useEffect, ReactNode, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useImages } from './ImagesProvider'; // Use images and related functionality from ImagesProvider
import type { Image } from './types';
import { Product, ProductImageItemInput, ProductVariantInput } from '@shopify-multiseller/graphql/partner/types';

interface ProductImagesContextProps {
    productId: string | null;
    selectedImageIds: string[];
    selectedImages: Image[];
    loading: boolean;
    loadingMore: boolean;
    fetchImages: () => Promise<void>;
    fetchMoreImages: () => Promise<void>;
    removeImage: (id: string) => void;
    selectImages: (ids: string[]) => void;
    clearImages: () => void;
    uploadImage: (file: File) => Promise<Image | void>;
    deleteImage: (id: string) => Promise<void>;
}

const ProductImagesContext = createContext<ProductImagesContextProps | undefined>(undefined);

export const ProductImagesProvider: React.FC<{ children: ReactNode; product: Product | null }> = ({ children, product }) => {
    const { images, uploadImage, fetchMoreImages, loading, loadingMore,  setImages } = useImages(); // Use ImagesProvider data
    const productId = product?.id || null;

    const { values, setFieldValue } = useFormikContext<{ images: ProductImageItemInput[], variants: ProductVariantInput[] }>();

    // Ensure selectedImageIds is always an array
    const selectedImageIds = useMemo(() => values.images ? values.images.map(img => img.id) : [], [values.images]);

    useEffect(() => {
        if (product) {
            const productImages = product.pending?.images ?? product.current?.images ?? [];
            const initialImages = productImages.map(img => ({
                id: img.id,
                url: img.image.url,
                thumbnailUrl: img.image.thumbnailUrl,
                loading: false,
                order: img.order,
                isFeatured: img.isFeatured,
            }));
            setImages(initialImages);
        }
    }, [product, setFieldValue]);

    useEffect(() => {
        if (values.variants) {
            // Collect all imageIds from the variants
            const variantImageIds = values.variants
                .map(variant => variant.imageId)
                .filter(id => id !== null && id !== undefined);

            // Find imageIds that are not already in selectedImageIds
            const newImageIds = variantImageIds.filter(id => !selectedImageIds.includes(id));

            if (newImageIds.length > 0) {
                // Append newImageIds to selectedImageIds
                const updatedImageIds = [...selectedImageIds, ...newImageIds];

                // Use selectImages to update the selected images
                selectImages(updatedImageIds);
            }
        }
    }, [values.variants, selectedImageIds]);

    // Updated selectedImages computation using useMemo
    const selectedImages = useMemo(() => {
        return selectedImageIds
            .map(id => images.find(img => img.id === id))
            .filter((img): img is Image => Boolean(img));
    }, [selectedImageIds, images]);

    const clearImages = () => {
        setFieldValue('images', []);
    };

    const selectImages = (ids: string[]) => {
        setFieldValue('images', ids.map((id, index) => ({
            id,
            isFeatured: index === 0,
            order: index,
        })));
    };

    const removeImage = (id: string) => {
        setFieldValue('images', values.images.filter(img => img.id !== id));
    };

    const deleteImage = async (id: string) => {
        removeImage(id);
        await fetchMoreImages(productId, selectedImageIds);
    };

    return (
        <ProductImagesContext.Provider value={{
            productId,
            selectedImageIds,
            selectedImages,
            loading,
            loadingMore,
            fetchImages: () => fetchMoreImages(productId, selectedImageIds),
            fetchMoreImages: () => fetchMoreImages(productId, selectedImageIds),
            removeImage,
            selectImages,
            clearImages,
            uploadImage: file => uploadImage(file, productId),
            deleteImage,
        }}>
            {children}
        </ProductImagesContext.Provider>
    );
};

export const useProductImages = () => {
    const context = useContext(ProductImagesContext);
    if (!context) throw new Error('useProductImages must be used within a ProductImagesProvider');
    return context;
};
